import _extends from "@babel/runtime/helpers/extends";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectDestructuringEmpty from "@babel/runtime/helpers/objectDestructuringEmpty";
import React, { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Redirect, useLocation, useHistory } from "react-router-dom";
import { Button, Loader } from "@components/atoms";
import { CheckoutProgressBar } from "@components/molecules";
import { PaymentGatewaysList, translateAdyenConfirmationError, adyenNotNegativeConfirmationStatusCodes } from "@components/organisms";
import { Checkout } from "@components/templates";
import { useCart, useCheckout } from "@lastmaj/saleor-sdk";
import { CHECKOUT_STEPS, CheckoutStep } from "@temp/core/config";
import { checkoutMessages } from "@temp/intl";
import { getNaiveShippingPrice, parseQueryString } from "@temp/core/utils";
import { CheckoutRouter } from "./CheckoutRouter";
import { CheckoutAddressSubpage, CheckoutPaymentSubpage, CheckoutReviewSubpage } from "./subpages";
import { cartUrl } from "@temp/app/routes";

var prepareCartSummaryProducts = function prepareCartSummaryProducts(items) {
  return items === null || items === void 0 ? void 0 : items.map(function (_ref) {
    var _variant$product, _variant$product2, _variant$product2$thu, _variant$product3, _variant$product3$thu, _variant$product4, _variant$product4$thu;

    var id = _ref.id,
        variant = _ref.variant,
        totalPrice = _ref.totalPrice,
        quantity = _ref.quantity;
    return {
      id: id || "",
      name: ((_variant$product = variant.product) === null || _variant$product === void 0 ? void 0 : _variant$product.name) || "",
      variantName: variant.name || "",
      price: {
        gross: {
          amount: (totalPrice === null || totalPrice === void 0 ? void 0 : totalPrice.gross.amount) || 0,
          currency: (totalPrice === null || totalPrice === void 0 ? void 0 : totalPrice.gross.currency) || ""
        },
        net: {
          amount: (totalPrice === null || totalPrice === void 0 ? void 0 : totalPrice.net.amount) || 0,
          currency: (totalPrice === null || totalPrice === void 0 ? void 0 : totalPrice.net.currency) || ""
        }
      },
      quantity: quantity,
      sku: variant.sku || "",
      thumbnail: {
        alt: ((_variant$product2 = variant.product) === null || _variant$product2 === void 0 ? void 0 : (_variant$product2$thu = _variant$product2.thumbnail) === null || _variant$product2$thu === void 0 ? void 0 : _variant$product2$thu.alt) || undefined,
        url: (_variant$product3 = variant.product) === null || _variant$product3 === void 0 ? void 0 : (_variant$product3$thu = _variant$product3.thumbnail) === null || _variant$product3$thu === void 0 ? void 0 : _variant$product3$thu.url,
        url2x: (_variant$product4 = variant.product) === null || _variant$product4 === void 0 ? void 0 : (_variant$product4$thu = _variant$product4.thumbnail2x) === null || _variant$product4$thu === void 0 ? void 0 : _variant$product4$thu.url
      }
    };
  });
};

var getCheckoutProgress = function getCheckoutProgress(loaded, activeStepIndex, steps) {
  return loaded ? React.createElement(CheckoutProgressBar, {
    steps: steps,
    activeStep: activeStepIndex
  }) : null;
};

var getButton = function getButton(text, onClick) {
  if (text) {
    return React.createElement(Button, {
      testingContext: "checkoutPageNextStepButton",
      onClick: onClick,
      type: "submit"
    }, text);
  }

  return null;
};

var CheckoutPage = function CheckoutPage(_ref2) {
  var _checkout$shippingMet, _checkout$shippingMet2, _buttonText;

  _objectDestructuringEmpty(_ref2);

  var location = useLocation();
  var history = useHistory();
  var querystring = parseQueryString(location);

  var _useCart = useCart(),
      cartLoaded = _useCart.loaded,
      shippingPrice = _useCart.shippingPrice,
      discount = _useCart.discount,
      subtotalPrice = _useCart.subtotalPrice,
      totalPriceCart = _useCart.totalPrice,
      items = _useCart.items;

  var _useCheckout = useCheckout(),
      checkoutLoaded = _useCheckout.loaded,
      checkout = _useCheckout.checkout,
      payment = _useCheckout.payment,
      availablePaymentGateways = _useCheckout.availablePaymentGateways,
      createPayment = _useCheckout.createPayment,
      completeCheckout = _useCheckout.completeCheckout;

  var intl = useIntl();

  if (cartLoaded && (!items || !(items === null || items === void 0 ? void 0 : items.length))) {
    return React.createElement(Redirect, {
      to: "/cart/"
    });
  }

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      submitInProgress = _useState2[0],
      setSubmitInProgress = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      paymentConfirmation = _useState4[0],
      setPaymentConfirmation = _useState4[1];

  var _useState5 = useState(payment === null || payment === void 0 ? void 0 : payment.gateway),
      _useState6 = _slicedToArray(_useState5, 2),
      selectedPaymentGateway = _useState6[0],
      setSelectedPaymentGateway = _useState6[1];

  var _useState7 = useState(payment === null || payment === void 0 ? void 0 : payment.token),
      _useState8 = _slicedToArray(_useState7, 2),
      selectedPaymentGatewayToken = _useState8[0],
      setSelectedPaymentGatewayToken = _useState8[1];

  var _useState9 = useState([]),
      _useState10 = _slicedToArray(_useState9, 2),
      paymentGatewayErrors = _useState10[0],
      setPaymentGatewayErrors = _useState10[1];

  useEffect(function () {
    setSelectedPaymentGateway(payment === null || payment === void 0 ? void 0 : payment.gateway);
  }, [payment === null || payment === void 0 ? void 0 : payment.gateway]);
  useEffect(function () {
    setSelectedPaymentGatewayToken(payment === null || payment === void 0 ? void 0 : payment.token);
  }, [payment === null || payment === void 0 ? void 0 : payment.token]);
  var isShippingRequiredForProducts = items && items.some(function (_ref3) {
    var _variant$product5;

    var variant = _ref3.variant;
    return (_variant$product5 = variant.product) === null || _variant$product5 === void 0 ? void 0 : _variant$product5.productType.isShippingRequired;
  });
  var stepsWithViews = CHECKOUT_STEPS.filter(function (_ref4) {
    var withoutOwnView = _ref4.withoutOwnView;
    return !withoutOwnView;
  });
  var steps = isShippingRequiredForProducts ? stepsWithViews : stepsWithViews.filter(function (_ref5) {
    var onlyIfShippingRequired = _ref5.onlyIfShippingRequired;
    return !onlyIfShippingRequired;
  });

  var getActiveStepIndex = function getActiveStepIndex() {
    var matchingStepIndex = steps.findIndex(function (_ref6) {
      var link = _ref6.link;
      return link === location.pathname;
    });
    return matchingStepIndex !== -1 ? matchingStepIndex : steps.length - 1;
  };

  var getActiveStep = function getActiveStep() {
    return steps[getActiveStepIndex()];
  };

  var checkoutAddressSubpageRef = useRef(null);
  var checkoutPaymentSubpageRef = useRef(null);
  var checkoutReviewSubpageRef = useRef(null);
  var checkoutGatewayFormId = "gateway-form";
  var checkoutGatewayFormRef = useRef(null);

  var handleNextStepClick = function handleNextStepClick() {
    var _checkoutAddressSubpa, _checkoutPaymentSubpa, _checkoutReviewSubpag;

    // Some magic above and below ensures that the activeStepIndex will always
    // be in 0-3 range

    /* eslint-disable default-case */
    switch (getActiveStep().index) {
      case 0:
        if ((_checkoutAddressSubpa = checkoutAddressSubpageRef.current) === null || _checkoutAddressSubpa === void 0 ? void 0 : _checkoutAddressSubpa.submitAddress) {
          var _checkoutAddressSubpa2;

          (_checkoutAddressSubpa2 = checkoutAddressSubpageRef.current) === null || _checkoutAddressSubpa2 === void 0 ? void 0 : _checkoutAddressSubpa2.submitAddress();
        }

        break;

      case 1:
        if ((_checkoutPaymentSubpa = checkoutPaymentSubpageRef.current) === null || _checkoutPaymentSubpa === void 0 ? void 0 : _checkoutPaymentSubpa.submitPayment) {
          var _checkoutPaymentSubpa2;

          (_checkoutPaymentSubpa2 = checkoutPaymentSubpageRef.current) === null || _checkoutPaymentSubpa2 === void 0 ? void 0 : _checkoutPaymentSubpa2.submitPayment();
        }

        break;

      case 2:
        if ((_checkoutReviewSubpag = checkoutReviewSubpageRef.current) === null || _checkoutReviewSubpag === void 0 ? void 0 : _checkoutReviewSubpag.complete) {
          var _checkoutReviewSubpag2;

          (_checkoutReviewSubpag2 = checkoutReviewSubpageRef.current) === null || _checkoutReviewSubpag2 === void 0 ? void 0 : _checkoutReviewSubpag2.complete();
        }

        break;
    }
  };

  var handleStepSubmitSuccess = function handleStepSubmitSuccess(currentStep, data) {
    var activeStepIndex = getActiveStepIndex();

    if (currentStep === CheckoutStep.Review) {
      if (data === null || data === void 0 ? void 0 : data.confirmationNeeded) {
        var json = JSON.parse(data === null || data === void 0 ? void 0 : data.confirmationData);
        window.location.replace(json.clickToPay.formUrl);
      } else {
        history.push({
          pathname: "/order-finalized",
          state: data
        });
      }
    } else {
      history.push(steps[activeStepIndex + 1].link);
    }
  };

  var shippingTaxedPrice = (checkout === null || checkout === void 0 ? void 0 : (_checkout$shippingMet = checkout.shippingMethod) === null || _checkout$shippingMet === void 0 ? void 0 : _checkout$shippingMet.id) && shippingPrice ? {
    gross: shippingPrice,
    net: shippingPrice
  } : getNaiveShippingPrice(subtotalPrice);
  var totalPrice = shippingPrice && ((checkout === null || checkout === void 0 ? void 0 : (_checkout$shippingMet2 = checkout.shippingMethod) === null || _checkout$shippingMet2 === void 0 ? void 0 : _checkout$shippingMet2.id) && shippingTaxedPrice ? totalPriceCart : {
    gross: {
      currency: "TND",
      amount: shippingTaxedPrice.gross.amount + subtotalPrice.gross.amount
    },
    net: {
      currency: "TND",
      amount: shippingTaxedPrice.net.amount + subtotalPrice.net.amount
    }
  });
  var promoTaxedPrice = discount && {
    gross: discount,
    net: discount
  };

  var handleProcessPayment =
  /*#__PURE__*/
  function () {
    var _ref7 = _asyncToGenerator(
    /*#__PURE__*/
    _regeneratorRuntime.mark(function _callee(gateway, token, amount, cardData) {
      var _CHECKOUT_STEPS$find;

      var paymentConfirmStepLink, localPayment;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              paymentConfirmStepLink = (_CHECKOUT_STEPS$find = CHECKOUT_STEPS.find(function (step) {
                return step.step === CheckoutStep.PaymentConfirm;
              })) === null || _CHECKOUT_STEPS$find === void 0 ? void 0 : _CHECKOUT_STEPS$find.link;
              localPayment = {
                gateway: gateway,
                token: token,
                creditCard: cardData,
                returnUrl: "".concat(window.location.origin).concat(paymentConfirmStepLink),
                currency: "TND",
                amount: amount
              };
              localStorage.setItem("payment", JSON.stringify(localPayment));
              setSubmitInProgress(false);
              handleStepSubmitSuccess(CheckoutStep.Payment);

            case 5:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));

    return function handleProcessPayment(_x, _x2, _x3, _x4) {
      return _ref7.apply(this, arguments);
    };
  }();

  var handleSubmitPayment =
  /*#__PURE__*/
  function () {
    var _ref8 = _asyncToGenerator(
    /*#__PURE__*/
    _regeneratorRuntime.mark(function _callee2(paymentData) {
      var _response$data, _response$data2, _response$data3, _response$dataError;

      var response;
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              _context2.next = 2;
              return completeCheckout({
                paymentData: paymentData
              });

            case 2:
              response = _context2.sent;
              return _context2.abrupt("return", {
                confirmationData: (_response$data = response.data) === null || _response$data === void 0 ? void 0 : _response$data.confirmationData,
                confirmationNeeded: (_response$data2 = response.data) === null || _response$data2 === void 0 ? void 0 : _response$data2.confirmationNeeded,
                order: (_response$data3 = response.data) === null || _response$data3 === void 0 ? void 0 : _response$data3.order,
                errors: (_response$dataError = response.dataError) === null || _response$dataError === void 0 ? void 0 : _response$dataError.error
              });

            case 4:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2);
    }));

    return function handleSubmitPayment(_x5) {
      return _ref8.apply(this, arguments);
    };
  }();

  var handleSubmitPaymentSuccess = function handleSubmitPaymentSuccess(order) {
    setSubmitInProgress(false);
    setPaymentGatewayErrors([]);
    handleStepSubmitSuccess(CheckoutStep.Review, {
      id: order === null || order === void 0 ? void 0 : order.id,
      orderNumber: order === null || order === void 0 ? void 0 : order.number,
      token: order === null || order === void 0 ? void 0 : order.token
    });
  };

  var handlePaymentGatewayError = function handlePaymentGatewayError(errors) {
    var _steps$find;

    setSubmitInProgress(false);
    setPaymentGatewayErrors(errors);
    var paymentStepLink = (_steps$find = steps.find(function (step) {
      return step.step === CheckoutStep.Payment;
    })) === null || _steps$find === void 0 ? void 0 : _steps$find.link;

    if (paymentStepLink) {
      history.push(paymentStepLink);
    }
  };

  var paymentGatewaysView = availablePaymentGateways && React.createElement(PaymentGatewaysList, {
    paymentGateways: availablePaymentGateways,
    processPayment: handleProcessPayment,
    submitPayment: handleSubmitPayment,
    submitPaymentSuccess: handleSubmitPaymentSuccess,
    formId: checkoutGatewayFormId,
    formRef: checkoutGatewayFormRef,
    selectedPaymentGateway: selectedPaymentGateway,
    selectedPaymentGatewayToken: selectedPaymentGatewayToken,
    selectPaymentGateway: setSelectedPaymentGateway,
    onError: handlePaymentGatewayError,
    errors: paymentGatewayErrors,
    total: totalPrice === null || totalPrice === void 0 ? void 0 : totalPrice.gross.amount
  });
  var checkoutView = cartLoaded && checkoutLoaded ? React.createElement(CheckoutRouter, {
    items: items,
    checkout: checkout,
    payment: payment,
    totalPrice: totalPrice,
    renderAddress: function renderAddress(props) {
      return React.createElement(CheckoutAddressSubpage, _extends({
        ref: checkoutAddressSubpageRef,
        changeSubmitProgress: setSubmitInProgress,
        onSubmitSuccess: function onSubmitSuccess() {
          return handleStepSubmitSuccess(CheckoutStep.Address);
        }
      }, props));
    },
    renderPayment: function renderPayment(props) {
      return React.createElement(CheckoutPaymentSubpage, _extends({
        ref: checkoutPaymentSubpageRef,
        paymentGatewayFormRef: checkoutGatewayFormRef,
        changeSubmitProgress: setSubmitInProgress,
        onSubmitSuccess: function onSubmitSuccess() {
          return handleStepSubmitSuccess(CheckoutStep.Payment);
        },
        onPaymentGatewayError: setPaymentGatewayErrors,
        paymentGateways: paymentGatewaysView
      }, props));
    },
    renderReview: function renderReview(props) {
      return React.createElement(CheckoutReviewSubpage, _extends({
        ref: checkoutReviewSubpageRef,
        paymentGatewayFormRef: checkoutGatewayFormRef,
        selectedPaymentGatewayToken: selectedPaymentGatewayToken,
        changeSubmitProgress: setSubmitInProgress,
        onSubmitSuccess: function onSubmitSuccess(data) {
          return handleStepSubmitSuccess(CheckoutStep.Review, data);
        }
      }, props));
    }
  }) : React.createElement(Loader, null);
  var activeStep = getActiveStep();
  var buttonText = activeStep.nextActionName;
  /* eslint-disable default-case */

  switch (activeStep.nextActionName) {
    case "Continue to Shipping":
      buttonText = intl.formatMessage(checkoutMessages.addressNextActionName);
      break;

    case "Continue to Payment":
      buttonText = intl.formatMessage(checkoutMessages.shippingNextActionName);
      break;

    case "Continue to Review":
      buttonText = intl.formatMessage(checkoutMessages.paymentNextActionName);
      break;

    case "Place order":
      buttonText = intl.formatMessage(checkoutMessages.reviewNextActionName);
      break;
  }

  useEffect(function () {
    var _CHECKOUT_STEPS$find2;

    var paymentConfirmStepLink = (_CHECKOUT_STEPS$find2 = CHECKOUT_STEPS.find(function (step) {
      return step.step === CheckoutStep.PaymentConfirm;
    })) === null || _CHECKOUT_STEPS$find2 === void 0 ? void 0 : _CHECKOUT_STEPS$find2.link;

    if (!submitInProgress && checkout && location.pathname === paymentConfirmStepLink && !paymentConfirmation) {
      console.log(".");
    }
  }, [location.pathname, querystring, submitInProgress, checkout]);

  var handlePaymentConfirm =
  /*#__PURE__*/
  function () {
    var _ref9 = _asyncToGenerator(
    /*#__PURE__*/
    _regeneratorRuntime.mark(function _callee3() {
      var _ref10, data, dataError, errors, _steps$find2, paymentStepLink, _data$order, _data$order2, _data$order3, _steps$find3, _paymentStepLink;

      return _regeneratorRuntime.wrap(function _callee3$(_context3) {
        while (1) {
          switch (_context3.prev = _context3.next) {
            case 0:
              // /**
              //  * Prevent proceeding in confirmation flow in case of gateways that don't support it to prevent unknown bugs.
              //  */
              // if (payment?.gateway !== "mirumee.payments.adyen") {
              //   const paymentStepLink = steps.find(
              //     step => step.step === CheckoutStep.Payment
              //   )?.link;
              //   if (paymentStepLink) {
              //     history.push(paymentStepLink);
              //   }
              // }
              setSubmitInProgress(true);
              setPaymentConfirmation(true);
              /**
               * Saleor API creates an order for not fully authorised payments, thus we accept all non negative payment result codes,
               * assuming the payment is completed, what means we can proceed further.
               * https://docs.adyen.com/checkout/drop-in-web?tab=http_get_1#step-6-present-payment-result
               */

              if (!adyenNotNegativeConfirmationStatusCodes.includes(querystring.resultCode)) {
                _context3.next = 13;
                break;
              }

              _context3.next = 5;
              return completeCheckout();

            case 5:
              _ref10 = _context3.sent;
              data = _ref10.data;
              dataError = _ref10.dataError;
              errors = dataError === null || dataError === void 0 ? void 0 : dataError.error;
              setSubmitInProgress(false);

              if (errors) {
                setPaymentGatewayErrors(errors);
                paymentStepLink = (_steps$find2 = steps.find(function (step) {
                  return step.step === CheckoutStep.Payment;
                })) === null || _steps$find2 === void 0 ? void 0 : _steps$find2.link;

                if (paymentStepLink) {
                  history.push(paymentStepLink);
                }
              } else {
                setPaymentGatewayErrors([]);
                handleStepSubmitSuccess(CheckoutStep.Review, {
                  id: data === null || data === void 0 ? void 0 : (_data$order = data.order) === null || _data$order === void 0 ? void 0 : _data$order.id,
                  orderNumber: data === null || data === void 0 ? void 0 : (_data$order2 = data.order) === null || _data$order2 === void 0 ? void 0 : _data$order2.number,
                  token: data === null || data === void 0 ? void 0 : (_data$order3 = data.order) === null || _data$order3 === void 0 ? void 0 : _data$order3.token
                });
              }

              _context3.next = 16;
              break;

            case 13:
              setPaymentGatewayErrors([{
                message: translateAdyenConfirmationError(querystring.resultCode, intl)
              }]);
              _paymentStepLink = (_steps$find3 = steps.find(function (step) {
                return step.step === CheckoutStep.Payment;
              })) === null || _steps$find3 === void 0 ? void 0 : _steps$find3.link;

              if (_paymentStepLink) {
                history.push(_paymentStepLink);
                setSubmitInProgress(false);
                setPaymentConfirmation(false);
              }

            case 16:
            case "end":
              return _context3.stop();
          }
        }
      }, _callee3);
    }));

    return function handlePaymentConfirm() {
      return _ref9.apply(this, arguments);
    };
  }();

  var activeStepIndex = getActiveStepIndex();
  var goBackLink = activeStepIndex === 0 ? cartUrl : steps[activeStepIndex - 1].link;
  return React.createElement(Checkout, _extends({
    loading: submitInProgress,
    navigation: getCheckoutProgress(cartLoaded && checkoutLoaded, activeStepIndex, steps),
    checkout: checkoutView,
    hidePaymentGateways: steps[activeStepIndex].step !== CheckoutStep.Payment,
    button: getButton((_buttonText = buttonText) === null || _buttonText === void 0 ? void 0 : _buttonText.toUpperCase(), handleNextStepClick),
    onButtonClick: handleNextStepClick,
    buttonText: buttonText,
    goBackLink: goBackLink,
    shippingPrice: shippingTaxedPrice,
    discountPrice: promoTaxedPrice,
    products: prepareCartSummaryProducts(items)
  }, {
    subtotalPrice: subtotalPrice,
    totalPrice: totalPrice
  }));
};

export { CheckoutPage };